import isEqual from 'lodash/isEqual';
import { useRef } from 'react';

/**
 * Returns a referentially-stable version of `value`, only updating the returned reference when
 * `value` is no longer deep-equal to the previously returned one.
 *
 * Useful for guarding a `useMemo`/`useCallback`/`useEffect` dependency against callers who pass
 * unmemoized inline objects or arrays, which would otherwise change reference every render and
 * defeat memoization even though their contents haven't changed.
 *
 * @param value - The value to stabilize. Compared to the previous value via `lodash/isEqual`.
 * @returns The previous reference if `value` is deep-equal to it, otherwise the new `value`.
 *
 * @example
 * ```tsx
 * function useSecurity(action: SecurityAction, target?: SecurityTarget) {
 *     // Even if callers pass `{ entityName: 'note' }` as a fresh object literal every render,
 *     // `stableTarget` only changes reference when its contents actually change.
 *     const stableTarget = useStableValue(target);
 *
 *     return useMemo(() => evaluateSecurityDecision(action, stableTarget), [action, stableTarget]);
 * }
 * ```
 */
export function useStableValue<T>(value: T): T {
    const ref = useRef(value);

    if (!isEqual(ref.current, value)) {
        ref.current = value;
    }

    return ref.current;
}
